import { GameObject } from './Scene.js';

// 动画类型
export type AnimationType = 'bounce' | 'shake' | 'pulse' | 'float';

// 动画实例
interface Animation {
  target: GameObject;
  type: AnimationType;
  startTime: number;
  duration: number;
  from: number;
  to: number;
  onComplete?: () => void;
}

// 缓动函数
const Easing = {
  easeOutBack: (t: number): number => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  
  easeOutQuad: (t: number): number => {
    return 1 - (1 - t) * (1 - t);
  },
  
  easeInOutQuad: (t: number): number => {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  },
  
  easeOutElastic: (t: number): number => {
    const c4 = (2 * Math.PI) / 3;
    return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
  
  linear: (t: number): number => t,
};

export class AnimationSystem {
  private animations: Animation[] = [];
  
  playClickAnimation(target: GameObject): void {
    // Scale弹动效果
    this.addAnimation({
      target,
      type: 'bounce',
      startTime: Date.now(),
      duration: 400,
      from: 1,
      to: 1,
    });
  }
  
  playShakeAnimation(target: GameObject): void {
    this.addAnimation({
      target,
      type: 'shake',
      startTime: Date.now(),
      duration: 300,
      from: 0,
      to: 0,
    });
  }
  
  playPulseAnimation(target: GameObject): void {
    this.addAnimation({
      target,
      type: 'pulse',
      startTime: Date.now(),
      duration: 600,
      from: 1,
      to: 1,
    });
  }
  
  private addAnimation(anim: Animation): void {
    // 移除同目标的同类型动画
    this.animations = this.animations.filter(
      a => !(a.target === anim.target && a.type === anim.type)
    );
    this.animations.push(anim);
  }
  
  update(_dt: number): void {
    const now = Date.now();
    
    this.animations = this.animations.filter(anim => {
      const elapsed = now - anim.startTime;
      const progress = Math.min(elapsed / anim.duration, 1);
      
      this.applyAnimation(anim, progress);
      
      if (progress >= 1) {
        if (anim.onComplete) anim.onComplete();
        return false;
      }
      return true;
    });
  }
  
  private applyAnimation(anim: Animation, progress: number): void {
    switch (anim.type) {
      case 'bounce':
        this.applyBounce(anim, progress);
        break;
      case 'shake':
        this.applyShake(anim, progress);
        break;
      case 'pulse':
        this.applyPulse(anim, progress);
        break;
    }
  }
  
  private applyBounce(anim: Animation, progress: number): void {
    // 点击弹动：快速缩小 -> 回弹放大 -> 恢复
    let scale: number;
    if (progress < 0.3) {
      // 快速缩小到0.85
      const p = progress / 0.3;
      scale = 1 - 0.15 * Easing.easeOutQuad(p);
    } else if (progress < 0.6) {
      // 回弹到1.15
      const p = (progress - 0.3) / 0.3;
      scale = 0.85 + 0.3 * Easing.easeOutBack(p);
    } else {
      // 恢复到1.0
      const p = (progress - 0.6) / 0.4;
      scale = 1.15 - 0.15 * Easing.easeOutQuad(p);
    }
    anim.target.scale = scale;
  }
  
  private applyShake(anim: Animation, progress: number): void {
    // 冷却中抖动：左右快速晃动
    // 注意：这里我们只修改scale来模拟抖动效果，不直接修改x坐标
    // 因为x坐标会影响渲染排序
    const shakeIntensity = 1 + Math.sin(progress * Math.PI * 8) * 0.05 * (1 - progress);
    anim.target.scale = shakeIntensity;
  }
  
  private applyPulse(anim: Animation, progress: number): void {
    // 脉冲效果
    const scale = 1 + 0.1 * Math.sin(progress * Math.PI * 2);
    anim.target.scale = scale;
  }
}

// 简单的Tween类，用于非GameObject对象的动画
export class Tween {
  private static tweens: Array<{
    target: any;
    property: string;
    from: number;
    to: number;
    startTime: number;
    duration: number;
    easing: keyof typeof Easing;
    onComplete?: () => void;
  }> = [];
  
  static animate(
    target: any,
    property: string,
    from: number,
    to: number,
    duration: number,
    easing: keyof typeof Easing = 'easeOutQuad',
    onComplete?: () => void
  ): void {
    this.tweens.push({
      target,
      property,
      from,
      to,
      startTime: Date.now(),
      duration,
      easing,
      onComplete,
    });
  }
  
  static update(): void {
    const now = Date.now();
    
    this.tweens = this.tweens.filter(tween => {
      const elapsed = now - tween.startTime;
      const progress = Math.min(elapsed / tween.duration, 1);
      const easedProgress = Easing[tween.easing](progress);
      
      tween.target[tween.property] = tween.from + (tween.to - tween.from) * easedProgress;
      
      if (progress >= 1) {
        if (tween.onComplete) tween.onComplete();
        return false;
      }
      return true;
    });
  }
}
